import * as React from 'react';
import {useFlag} from '@sail/access';
import {DevNotice} from '../../components/DevNotice';
import type {ConnectElementImportKeys} from '../../connect/ConnectJSInterface/ConnectElementList';
import {useEmitEvent} from '../EventEmitterContext';
import {messages as devNoticeMessages} from '../../components/DevNotice/messages';
import {useAuthContext} from '../AuthContext/AuthContext';
import type {MetaOptions} from '../../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';

type Props = {
  children: React.ReactNode;
  connectElement: ConnectElementImportKeys;
  metaOptions: MetaOptions;
};

/**
 * This component enforces that platforms are gated into enable_connect_mobile_sdk_private_preview when the Mobile SDK is being used
 */
export const MobileSdkPreviewGateEnforcer: React.FC<Props> = ({
  children,
  connectElement,
  metaOptions,
}) => {
  const {authInfo} = useAuthContext();
  const isGatedIntoMobileSdkPreview = useFlag(
    'enable_connect_mobile_sdk_private_preview',
  );

  // This check matches how we set metaOptions in the SDK Webview - frontend/connect/submerchant-surfaces/src/utils/mobile-sdk/getMobileSdkMetaOptions.ts
  const tokenOverridesAreSet =
    metaOptions.livemodeOverride !== undefined &&
    metaOptions.apiKeyOverride &&
    metaOptions.merchantIdOverride &&
    metaOptions.platformIdOverride;

  const isMobileSdk = metaOptions.mobileSdk;

  const emitEvent = useEmitEvent();

  const shouldBlockRendering = !!(
    isMobileSdk &&
    !(isGatedIntoMobileSdkPreview || tokenOverridesAreSet) &&
    authInfo
  );

  React.useEffect(() => {
    if (shouldBlockRendering) {
      // Emit a load error event to the platform with the relevant message
      emitEvent('loaderror', {
        error: {
          type: 'invalid_request_error',
          message:
            devNoticeMessages.mustBeGatedIntoMobileSdkPreview.defaultMessage,
        },
      });
    }
  }, [shouldBlockRendering, emitEvent]);

  if (shouldBlockRendering) {
    // We render nothing (this notice renders only for developers)
    return (
      <DevNotice
        type="mustBeGatedIntoMobileSdkPreview"
        connectElement={connectElement}
      />
    );
  }

  // Simple pass through
  return <>{children}</>;
};
